import React, { useMemo } from 'react';

import { IonCol, IonGrid, IonRow } from '@ionic/react';

import { Accordion, AccordionGroup } from 'components/Accordion';
import { DescriptionItem, DescriptionList } from 'components/DescriptionList';
import Typography from 'components/Typography';
import { useCurrencyFormat } from 'intl';
import ApproverListItem from 'scenes/FinancePage/FR/Details/components/Details/components/Approver';
import Status from 'scenes/FinancePage/FR/Details/components/Status';

import IOList from './components/IOList';
import StatsDataTable from './components/StatsDataTable';

export default ({ data }: { data: any }) => {
  const currencyFormat = useCurrencyFormat();
  const IOCount = useMemo(
    () => data?.spend?.io?.length || 0,
    [data?.spend?.io]
  );

  const budgetOverrun = useMemo(
    () => data?.totalInvoicesAmount?.amount - data?.budget?.amount,
    [data?.budget?.amount, data?.totalInvoicesAmount?.amount]
  );
  const isBudgetOverrun = data?.spend?.io?.length > 0 && budgetOverrun > 0;
  return (
    <AccordionGroup>
      <Accordion>
        <DescriptionList data={data}>
          <IonGrid>
            <IonRow>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'period'}
                  label={'Period'}
                  type={'dateRange'}
                  template={'MONTH'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'status'}
                  label={'Status'}
                  type={'status'}
                  statusFactory={Status}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'dueDate'}
                  label={'Due date'}
                  template={'LL'}
                  type={'date'}
                />
              </IonCol>
              <IonCol size={'6'}>
                {data?.businessPartner?.name && !data?.affiliates?.length ? (
                  <DescriptionItem
                    field={'businessPartner.name'}
                    label={'Business Partner'}
                  />
                ) : (
                  <DescriptionItem
                    field={'affiliates'}
                    unitsLabel={'Affiliates'}
                    type={'typographyList'}
                    limit={3}
                    label={'Affiliates'}
                  />
                )}
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'customerCompany.name'}
                  label={'Customer Company'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'applications'}
                  unitsLabel={'Applications'}
                  type={'typographyList'}
                  limit={3}
                  label={'Applications'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'expenseType.name'}
                  label={'Expense Type'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'currencyCode'}
                  label={'Currency'}
                  className={'ion-text-uppercase'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'importance.name'}
                  label={'Importance'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'spend.spend'}
                  label={'Spend'}
                  type={'amount'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'spend.originalSpend'}
                  label={'Original Spend'}
                  type={'amount'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  field={'totalInvoicesAmount'}
                  label={'Total Invoices Amount'}
                  type={'amount'}
                />
                {isBudgetOverrun ? (
                  <Typography variant={'caption'} color={'danger'}>
                    Exceeds IO monthly budget by {currencyFormat(budgetOverrun)}
                  </Typography>
                ) : null}
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  label={'Approvers'}
                  field={'historyApprovers'}
                  type={'typographyList'}
                  item={ApproverListItem}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  label={'Last Updated By'}
                  type={'metadata'}
                  value={data}
                  userField={'editMetadata.lastUpdatedBy'}
                  dateField={'editMetadata.lastUpdatedDate'}
                />
              </IonCol>
              <IonCol size={'6'}>
                <DescriptionItem
                  label={'Created By'}
                  type={'metadata'}
                  value={data}
                  userField={'editMetadata.createdBy'}
                  dateField={'editMetadata.createdDate'}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        </DescriptionList>
      </Accordion>
      {IOCount > 0 ? (
        <Accordion title={`Current IOs (${IOCount})`}>
          <IOList data={data?.spend?.io} isBudgetOverrun={isBudgetOverrun} />
        </Accordion>
      ) : null}
      <Accordion title={'Summary by Applications'}>
        <StatsDataTable data={data?.stats} />
      </Accordion>
    </AccordionGroup>
  );
};
